<template>
  <div v-if="visible" class="settlement-overlay">
    <div class="settlement-dialog">
      <div class="settlement-header">
        <h5 class="settlement-title">{{ title }}</h5>
        <button type="button" class="close-btn" @click="requestClose">
          &times;
        </button>
      </div>

      <div class="settlement-subheader">
        <div class="show-filter">
          <label>Pokaż:</label>
          <div
            class="show-filter-select"
            :class="{ 'select-disabled': isEmpty || submitting }"
          >
            <custom-select
              :key="`show-${allCount}-${readyCount}-${notReadyCount}`"
              :options="showFilterOptions"
              :prePick="showFilter"
              :graySelect="true"
              :large="true"
              name="settlement-show-filter"
              @newPick="(v) => (showFilter = v)"
            />
          </div>
        </div>
        <div class="counters">
          <span
            ><strong>Il. godz. zleconych:</strong> {{ counters.ordered }}</span
          >
          <span
            ><strong>Il. godz. wykonanych:</strong> {{ counters.made }}</span
          >
          <span
            ><strong>Il. godz. niezrealizowanych:</strong>
            {{ counters.unrealized }}</span
          >
        </div>
        <label
          class="mark-all"
          :class="{ 'mark-all-disabled': isEmpty || submitting }"
          v-if="canEdit"
        >
          <input
            type="checkbox"
            :checked="allReady"
            :disabled="isEmpty || submitting"
            @change="markAllReady($event)"
          />
          Oznacz wszystkie - gotowe
        </label>
      </div>

      <div v-if="isManager && submittedInfo" class="submitted-info">
        Przekazano do weryfikacji: {{ submittedInfo }}
      </div>

      <div class="settlement-body">
        <p v-if="!loading && tables.length === 0" class="empty">
          Brak zleceń w tym miesiącu.
        </p>
        <settlement-order-table
          v-for="table in filteredTables"
          :key="table.orderId"
          :table="table"
          :readonly="!canEdit"
          :disabled="submitting"
          @update-hours="onUpdateHours"
          @toggle-ready="onToggleReady"
        />
      </div>

      <div class="settlement-footer">
        <button
          v-if="showReturn"
          type="button"
          class="btn-return"
          :disabled="isEmpty || submitting"
          @click="askReturn"
        >
          Cofnij do poprawy
        </button>
        <button
          v-if="showSave"
          type="button"
          class="btn-save"
          :disabled="!dirty || isEmpty || submitting"
          @click="save"
        >
          Zapisz
        </button>
        <button
          v-if="showSubmit"
          type="button"
          class="btn-submit"
          :disabled="!allReady || isEmpty || submitting"
          @click="askSubmit"
        >
          Przekaż do weryfikacji
        </button>
        <button
          v-if="showSettle"
          type="button"
          class="btn-settle"
          :disabled="!allReady || isEmpty || submitting"
          @click="askSettle"
        >
          Rozlicz
        </button>
      </div>
    </div>

    <confirm-modal
      v-if="confirm"
      :message="confirm.message"
      :confirm-label="confirm.confirmLabel || 'Zatwierdź'"
      :cancel-label="confirm.cancelLabel || 'Anuluj'"
      :secondary-label="confirm.secondaryLabel || ''"
      @confirm="onConfirmAccept"
      @secondary="onConfirmSecondary"
      @cancel="onConfirmCancel"
    />
  </div>
</template>

<script>
import SettlementOrderTable from "./SettlementOrderTable.vue";
import ConfirmModal from "./ConfirmModal.vue";
import CustomSelect from "../../CustomSelect.vue";

const MONTHS = [
  "styczeń",
  "luty",
  "marzec",
  "kwiecień",
  "maj",
  "czerwiec",
  "lipiec",
  "sierpień",
  "wrzesień",
  "październik",
  "listopad",
  "grudzień",
];

export default {
  name: "SettlementModal",
  components: { SettlementOrderTable, ConfirmModal, CustomSelect },
  props: {
    visible: { type: Boolean, default: false },
    employeeId: { type: String, default: null },
    month: { type: Number, default: null },
    year: { type: Number, default: null },
    mode: { type: String, default: "employee" }, // 'employee' | 'manager'
  },
  emits: ["close", "changed"],
  data() {
    return {
      loading: false,
      tables: [],
      status: "NIEROZLICZONO",
      employeeName: "",
      submittedAt: null,
      submittedByName: null,
      permissions: {},
      showFilter: "ALL",
      dirty: false,
      confirm: null,
      submitting: false,
      // Snapshot z chwili wczytania: czy zapisany stan miał wszystkie zlecenia "Gotowe".
      // Potrzebny, by wiedzieć, czy "Przekaż bez zapisywania" jest wykonalne (backend
      // sprawdza gotowość z bazy, nie z bieżącego formularza).
      initialAllReady: false,
    };
  },
  computed: {
    isManager() {
      return this.mode === "manager";
    },
    canEdit() {
      return !!this.permissions.canEdit;
    },
    // Brak zleceń w miesiącu - blokujemy select, checkbox i wszystkie przyciski.
    isEmpty() {
      return this.tables.length === 0;
    },
    title() {
      const period = this.month ? `${MONTHS[this.month - 1]} ${this.year}` : "";
      if (this.isManager) {
        const verb =
          this.status === "DO_WERYFIKACJI"
            ? "Weryfikacja rozliczania"
            : "Rozliczanie";
        return `${verb} opiekuna ${this.employeeName} - ${period}`;
      }
      return `Wstępne rozliczenie opiekuna ${this.employeeName} - ${period}`;
    },
    filteredTables() {
      if (this.showFilter === "READY")
        return this.tables.filter((t) => t.isReady);
      if (this.showFilter === "NOT_READY")
        return this.tables.filter((t) => !t.isReady);
      return this.tables;
    },
    allCount() {
      return this.tables.length;
    },
    readyCount() {
      return this.tables.filter((t) => t.isReady).length;
    },
    notReadyCount() {
      return this.allCount - this.readyCount;
    },
    showFilterOptions() {
      return [
        {
          name: `Wszystkie (${this.allCount})`,
          shortName: `Wszystkie (${this.allCount})`,
          value: "ALL",
        },
        {
          name: `Gotowe (${this.readyCount})`,
          shortName: `Gotowe (${this.readyCount})`,
          value: "READY",
        },
        {
          name: `Niegotowe (${this.notReadyCount})`,
          shortName: `Niegotowe (${this.notReadyCount})`,
          value: "NOT_READY",
        },
      ];
    },
    allReady() {
      return this.tables.length > 0 && this.tables.every((t) => t.isReady);
    },
    // "Przekaż bez zapisywania" przekazuje zapisany w bazie stan (pomija bieżące zmiany).
    // Backend zaakceptuje to tylko, gdy rekord już istnieje (W_TRAKCIE/DO_POPRAWY) i był
    // zapisany jako wszystkie "Gotowe". Dla świeżego rozliczenia lub gdy część oznaczeń
    // "Gotowe" jest jeszcze niezapisana - jedyną drogą jest "Zapisz i przekaż".
    canSubmitWithoutSave() {
      return (
        (this.status === "W_TRAKCIE" || this.status === "DO_POPRAWY") &&
        this.initialAllReady
      );
    },
    counters() {
      let ordered = 0;
      let made = 0;
      this.tables.forEach((t) => {
        t.days.forEach((d) => {
          ordered += Number(d.ordered || 0);
          made += Number(d.made || 0);
        });
      });
      return { ordered, made, unrealized: ordered - made };
    },
    submittedInfo() {
      if (!this.submittedAt) return "";
      const d = new Date(this.submittedAt);
      return `${this.pad(d.getHours())}:${this.pad(d.getMinutes())} ${this.pad(
        d.getDate()
      )}.${this.pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    },
    showSave() {
      return this.canEdit;
    },
    // Action buttons are gated on the backend-computed `canEdit`, which already
    // folds in role + closed-month + main-admin bypass. This keeps the modal
    // consistent with the backend (a closed month shows a read-only modal for a
    // regular admin, but stays editable for a main admin).
    showSubmit() {
      return (
        !this.isManager &&
        this.canEdit &&
        this.status !== "ROZLICZONO" &&
        this.status !== "DO_WERYFIKACJI"
      );
    },
    showSettle() {
      return this.isManager && this.canEdit && this.status !== "ROZLICZONO";
    },
    showReturn() {
      // Backend `canReturn` już zawiera właściwą regułę: kierownik może cofnąć do poprawy
      // z DO_WERYFIKACJI LUB ROZLICZONO, ale nie gdy miesiąc jest za datą blokady zmian (strict).
      return this.isManager && !!this.permissions.canReturn;
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.fetch();
      }
    },
  },
  methods: {
    pad(n) {
      return String(n).padStart(2, "0");
    },
    async fetch() {
      this.loading = true;
      this.dirty = false;
      this.showFilter = "ALL";
      this.confirm = null;
      // Reset before fetching - the modal is a reused singleton, so on a failed
      // fetch we must not keep the previous employee's tables/status/permissions.
      this.tables = [];
      this.status = "NIEROZLICZONO";
      this.employeeName = "";
      this.submittedAt = null;
      this.submittedByName = null;
      this.permissions = {};
      try {
        const data = await this.$store.dispatch("getEmployeeSettlement", {
          employeeId: this.employeeId,
          month: this.month,
          year: this.year,
        });
        this.status = data.status;
        this.employeeName = data.employeeName;
        this.submittedAt = data.submittedAt;
        this.submittedByName = data.submittedByName;
        this.permissions = data.permissions || {};
        // Deep copy tables so local edits don't mutate the store response.
        this.tables = (data.tables || []).map((t) => ({
          orderId: t.orderId,
          customerName: t.customerName,
          departmentName: t.departmentName,
          isReady: t.isReady,
          days: t.days.map((d) => ({ ...d })),
        }));
        this.initialAllReady =
          this.tables.length > 0 && this.tables.every((t) => t.isReady);
      } catch (e) {
        this.$notify({
          group: "error",
          text: "Nie udało się pobrać rozliczenia",
        });
      } finally {
        this.loading = false;
      }
    },
    onUpdateHours({ orderId, orderDayId, value }) {
      const table = this.tables.find((t) => t.orderId === orderId);
      if (!table) return;
      const day = table.days.find((d) => d.orderDayId === orderDayId);
      if (!day) return;
      day.made = value;
      this.dirty = true;
    },
    onToggleReady({ orderId, isReady }) {
      const table = this.tables.find((t) => t.orderId === orderId);
      if (!table) return;
      table.isReady = isReady;
      this.dirty = true;
    },
    markAllReady(event) {
      const checked = event.target.checked;
      this.tables.forEach((t) => {
        t.isReady = checked;
      });
      this.dirty = true;
    },
    buildDraftPayload() {
      return {
        tables: this.tables.map((t) => ({
          orderId: t.orderId,
          isReady: t.isReady,
          days: t.days.map((d) => ({
            orderDayId: d.orderDayId,
            numberOfMadeHours:
              d.made == null || d.made === "" ? null : Number(d.made),
          })),
        })),
      };
    },
    async save() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        await this.$store.dispatch("saveSettlementDraft", {
          employeeId: this.employeeId,
          month: this.month,
          year: this.year,
          tables: this.buildDraftPayload().tables,
        });
        this.dirty = false;
        this.$notify({ group: "success", text: "Zapisano rozliczenie" });
        this.$emit("changed");
        await this.fetch();
      } catch (e) {
        this.notifyError(e);
      } finally {
        this.submitting = false;
      }
    },
    askSubmit() {
      if (!this.dirty) {
        this.confirm = {
          message:
            "Czy na pewno chcesz przekazać wstępne rozliczenie do weryfikacji?",
          action: "submit",
        };
        return;
      }
      // Niezapisane zmiany - jawny wybór (analogicznie do "Cofnij do poprawy" u kierownika):
      // "Zapisz i przekaż" zapisuje bieżący formularz i przekazuje; "Przekaż bez zapisywania"
      // przekazuje wcześniej zapisany stan, odrzucając bieżące zmiany.
      this.confirm = {
        message:
          "Masz <b>niezapisane zmiany</b> w tym rozliczeniu. Możesz je zapisać i przekazać " +
          "kierownikowi do weryfikacji, albo przekazać wcześniej zapisany stan " +
          "(bieżące zmiany zostaną wtedy odrzucone).",
        action: "submit",
        confirmLabel: "Zapisz i przekaż do weryfikacji",
        secondaryLabel: "Przekaż do weryfikacji bez zapisywania",
        cancelLabel: "Anuluj",
      };
    },
    askSettle() {
      this.confirm = {
        message: "Czy na pewno chcesz rozliczyć opiekuna?",
        action: "settle",
      };
    },
    askReturn() {
      if (this.dirty) {
        // Kierownik zmienił coś w rozliczeniu (godziny / oznaczenia "Gotowe") i nie zapisał.
        // Musi świadomie wybrać, czy zapisać zmiany, czy cofnąć bez nich.
        this.confirm = {
          message:
            "W tym rozliczeniu masz <b>niezapisane zmiany</b> (np. edytowane godziny lub oznaczenia „Gotowe”). " +
            "Jeśli cofniesz do poprawy bez zapisywania, rozliczenie wróci do opiekuna dokładnie w takim stanie, " +
            "w jakim je otrzymałeś - Twoje zmiany zostaną odrzucone. Czy zapisać zmiany przed cofnięciem do poprawy?",
          action: "return",
          dirty: true,
          confirmLabel: "Zapisz i cofnij do poprawy",
          secondaryLabel: "Cofnij bez zapisywania",
          cancelLabel: "Anuluj",
        };
      } else {
        this.confirm = {
          message: "Czy na pewno chcesz cofnąć rozliczenie do poprawy?",
          action: "return",
        };
      }
    },
    async onConfirmAccept() {
      const conf = this.confirm;
      this.confirm = null;
      if (!conf) return;
      if (conf.action === "submit") await this.doSubmit();
      else if (conf.action === "settle") await this.doSettle();
      else if (conf.action === "return") {
        // Przy niezapisanych zmianach przycisk główny = "Zapisz i cofnij".
        if (conf.dirty) await this.doReturnWithSave();
        else await this.doReturn();
      } else if (conf.action === "close") this.forceClose();
    },
    // Trzeci przycisk (secondary): akcja "bez zapisywania" - odrzuca bieżące zmiany.
    // Dla "return" (kierownik) cofa do poprawy; dla "submit" (pracownik) przekazuje zapisany stan.
    async onConfirmSecondary() {
      const conf = this.confirm;
      this.confirm = null;
      if (!conf) return;
      if (conf.action === "return") await this.doReturn();
      else if (conf.action === "submit") await this.doSubmit(false);
    },
    onConfirmCancel() {
      this.confirm = null;
    },
    async doSubmit(save = true) {
      if (this.submitting) return;
      // "Przekaż bez zapisywania" ma sens tylko, gdy w bazie jest już zapisany, kompletny stan.
      // Inaczej (świeże rozliczenie lub niezapisane oznaczenia "Gotowe") backend by odrzucił -
      // dajemy więc czytelny komunikat zamiast surowego błędu z API.
      if (!save && !this.canSubmitWithoutSave) {
        this.$notify({
          group: "error",
          text:
            "Nie można przekazać bez zapisywania - wcześniej zapisany stan nie jest kompletny " +
            "(nie wszystkie zlecenia były zapisane jako „Gotowe” albo rozliczenie nie było jeszcze " +
            "zapisane). Użyj „Zapisz i przekaż do weryfikacji”.",
        });
        return;
      }
      this.submitting = true;
      try {
        await this.$store.dispatch("submitSettlement", {
          employeeId: this.employeeId,
          month: this.month,
          year: this.year,
          draft: save ? this.buildDraftPayload() : undefined,
        });
        this.$notify({ group: "success", text: "Przekazano do weryfikacji" });
        this.$emit("changed");
        this.forceClose();
      } catch (e) {
        this.notifyError(e);
      } finally {
        this.submitting = false;
      }
    },
    async doSettle() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        await this.$store.dispatch("settleSettlement", {
          employeeId: this.employeeId,
          month: this.month,
          year: this.year,
          draft: this.buildDraftPayload(),
        });
        this.$notify({ group: "success", text: "Opiekun rozliczony" });
        this.$emit("changed");
        this.forceClose();
      } catch (e) {
        this.notifyError(e);
      } finally {
        this.submitting = false;
      }
    },
    async doReturn() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        await this.$store.dispatch("returnSettlementToFix", {
          employeeId: this.employeeId,
          month: this.month,
          year: this.year,
        });
        this.$notify({ group: "success", text: "Cofnięto do poprawy" });
        this.$emit("changed");
        this.forceClose();
      } catch (e) {
        this.notifyError(e);
      } finally {
        this.submitting = false;
      }
    },
    // Zapisuje zmiany kierownika, a następnie cofa rozliczenie do poprawy.
    async doReturnWithSave() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        await this.$store.dispatch("saveSettlementDraft", {
          employeeId: this.employeeId,
          month: this.month,
          year: this.year,
          tables: this.buildDraftPayload().tables,
        });
        await this.$store.dispatch("returnSettlementToFix", {
          employeeId: this.employeeId,
          month: this.month,
          year: this.year,
        });
        this.$notify({ group: "success", text: "Zapisano i cofnięto do poprawy" });
        this.$emit("changed");
        this.forceClose();
      } catch (e) {
        this.notifyError(e);
      } finally {
        this.submitting = false;
      }
    },
    requestClose() {
      if (this.dirty) {
        this.confirm = {
          message: "Czy na pewno chcesz wyjść bez zapisywania zmian?",
          action: "close",
        };
        return;
      }
      this.forceClose();
    },
    forceClose() {
      this.confirm = null;
      this.$emit("close");
    },
    notifyError(e) {
      const text =
        e && e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : "Wystąpił błąd";
      this.$notify({ group: "error", text });
    },
  },
};
</script>

<style scoped>
.settlement-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1050;
  padding: 40px 16px;
  overflow-y: auto;
}
.settlement-dialog {
  background: #fff;
  border-radius: 8px;
  width: 1100px;
  max-width: 96vw;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}
.settlement-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
}
.settlement-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  text-align: center;
}
.close-btn {
  position: absolute;
  right: 16px;
  top: 10px;
  border: none;
  background: none;
  font-size: 32px;
  font-weight: 200;
  cursor: pointer;
  line-height: 1;
}
.settlement-subheader {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  padding: 0px 24px;
  font-size: 12px;
  border-bottom: 1px solid #f2f2f2;
}
.show-filter {
  display: flex;
  align-items: center;
  gap: 8px;
}
.show-filter-select {
  width: 190px;
  position: relative;
}
.select-disabled {
  pointer-events: none;
  opacity: 0.5;
}
.counters {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
}
.mark-all {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  cursor: pointer;
  font-weight: 600;
}
.mark-all-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.mark-all-disabled input[type="checkbox"] {
  cursor: not-allowed;
}
/* Gradientowy checkbox - ten sam wzorzec co w DodFiltersCheckboxes / tabelce. */
.mark-all input[type="checkbox"] {
  position: relative;
  width: 20px;
  height: 20px;
  appearance: none;
  -webkit-appearance: none;
  background-color: transparent;
  outline: none;
  margin: 0;
  cursor: pointer;
}
.mark-all input[type="checkbox"]::after {
  content: "";
  width: 18px;
  height: 18px;
  position: absolute;
  top: 0;
  left: 0;
  background: white;
  border: 1px solid #090741;
  border-radius: 4px;
}
.mark-all input[type="checkbox"]:checked::after {
  content: "✔";
  display: inline-block;
  border: unset;
  background: linear-gradient(
    90deg,
    rgba(251, 134, 42, 1) 0%,
    rgba(241, 76, 82, 1) 49%,
    rgba(229, 8, 129, 1) 100%
  );
  font-size: 14px;
  line-height: 18px;
  text-align: center;
  color: white;
}
.submitted-info {
  padding: 6px 24px;
  font-size: 12px;
  color: #666;
}
.settlement-body {
  padding: 12px 24px;
  max-height: 60vh;
  overflow-y: auto;
}
.empty {
  text-align: center;
  color: #999;
  padding: 24px;
}
.settlement-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 24px;
  border-top: 1px solid #eee;
}
.settlement-footer button {
  border: none;
  border-radius: 6px;
  padding: 9px 22px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
}
.btn-return,
.btn-save {
  background: #090741;
}
.btn-submit,
.btn-settle {
  background: linear-gradient(90deg, #fb8928, #e50582);
}
.settlement-footer button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
